import type {
  EnvironmentId,
  PullRequestActivity,
  PullRequestComment,
  PullRequestDetail,
  PullRequestRef,
} from "@cmmd-forge/contracts";
import { ArrowDownUpIcon, HammerIcon } from "lucide-react";
import { useState } from "react";

import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { cmmdApiClient } from "~/environments/cmmdApi";
import { commandPullRequestRef } from "~/lib/pullRequestReactQuery";
import { usePullRequestHandoffController } from "~/pullRequest/handoff/PullRequestHandoffProvider";
import { canEditPullRequestComment } from "../pullRequestEditing.logic";
import { PullRequestTimelineItem } from "../timeline/PullRequestTimelineItem";
import { usePullRequestSerializedCommand } from "../timeline/usePullRequestSerializedCommand";
import { PullRequestCommentComposer } from "./PullRequestCommentComposer";
import { PullRequestSummarySection } from "./PullRequestSummarySection";

const COMMENT_PAGE_SIZE = 30;

export function PullRequestSummaryConversation(props: {
  readonly detail: PullRequestDetail;
  readonly activity: PullRequestActivity | undefined;
  readonly pending: boolean;
  readonly error: Error | null;
  readonly onRetry?: (() => void) | undefined;
  readonly environmentId: EnvironmentId;
  readonly reference: PullRequestRef;
  readonly isDetailFresh: boolean;
}) {
  const [shown, setShown] = useState({ url: props.detail.url, count: COMMENT_PAGE_SIZE });
  const [order, setOrder] = useState<"newest" | "oldest">("newest");
  const comments = props.activity?.comments ?? [];
  const shownCount = shown.url === props.detail.url ? shown.count : COMMENT_PAGE_SIZE;
  const recent = comments.slice(Math.max(0, comments.length - shownCount));
  const visible = order === "newest" ? recent.toReversed() : recent;
  const hidden = comments.length - recent.length;
  const canComment =
    props.detail.capabilities.comment &&
    props.detail.viewerPermissions.comment &&
    props.isDetailFresh;

  return (
    <PullRequestSummarySection
      title="Comments"
      {...(props.activity?.commentCount === undefined
        ? {}
        : { count: props.activity.commentCount })}
      actions={<ConversationOrderAction comments={comments} order={order} onChange={setOrder} />}
    >
      <div aria-label="Comments conversation">
        <ConversationTruncationNotice
          truncated={props.activity?.commentsTruncated === true}
          count={comments.length}
        />
        {hidden > 0 ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="mb-3 w-full"
            onClick={() =>
              setShown((current) => ({
                url: props.detail.url,
                count:
                  (current.url === props.detail.url ? current.count : COMMENT_PAGE_SIZE) +
                  COMMENT_PAGE_SIZE,
              }))
            }
          >
            Show {Math.min(hidden, COMMENT_PAGE_SIZE)} earlier{" "}
            {hidden === 1 ? "comment" : "comments"}
          </Button>
        ) : null}
        <ConversationItems comments={visible} {...props} />
        {canComment ? (
          <PullRequestCommentComposer
            environmentId={props.environmentId}
            reference={props.reference}
          />
        ) : null}
      </div>
    </PullRequestSummarySection>
  );
}

function ConversationOrderAction(props: {
  readonly comments: ReadonlyArray<PullRequestComment>;
  readonly order: "newest" | "oldest";
  readonly onChange: (order: "newest" | "oldest") => void;
}) {
  if (props.comments.length === 0) return null;
  const newest = props.order === "newest";
  return (
    <Button
      type="button"
      size="xs"
      variant="ghost"
      className="h-7 px-2 text-[10px] text-muted-foreground"
      aria-label={newest ? "Show oldest comments first" : "Show newest comments first"}
      onClick={() => props.onChange(newest ? "oldest" : "newest")}
    >
      <ArrowDownUpIcon aria-hidden className="size-3" />
      {newest ? "Newest first" : "Oldest first"}
    </Button>
  );
}

function ConversationTruncationNotice(props: {
  readonly truncated: boolean;
  readonly count: number;
}) {
  if (!props.truncated) return null;
  return (
    <p className="mb-2 rounded-md border border-warning-border bg-warning-surface px-2 py-1.5 text-xs text-warning-foreground">
      This conversation is longer than this page reads in one go. The most recent {props.count}{" "}
      {props.count === 1 ? "comment is" : "comments are"} here; open it on the provider to read the
      rest.
    </p>
  );
}

function ConversationItems(
  props: Parameters<typeof PullRequestSummaryConversation>[0] & {
    readonly comments: ReadonlyArray<PullRequestComment>;
  },
) {
  if (props.pending) {
    return (
      <div className="space-y-2 py-2" aria-label="Loading conversation" aria-busy="true">
        <Skeleton className="h-3 w-1/3" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }
  if (props.error !== null) {
    return (
      <div className="flex items-center justify-between gap-3 py-2">
        <p className="text-xs text-muted-foreground">Comments are temporarily unavailable.</p>
        {props.onRetry === undefined ? null : (
          <Button type="button" size="xs" variant="outline" onClick={props.onRetry}>
            Retry
          </Button>
        )}
      </div>
    );
  }
  if (props.comments.length === 0) {
    return <p className="py-2 text-xs text-muted-foreground">No comments yet.</p>;
  }
  return (
    <div className="space-y-3">
      {props.comments.map((comment) => (
        <PullRequestSummaryComment key={comment.id} comment={comment} {...props} />
      ))}
    </div>
  );
}

function PullRequestSummaryComment(props: {
  readonly comment: PullRequestComment;
  readonly detail: PullRequestDetail;
  readonly environmentId: EnvironmentId;
  readonly reference: PullRequestRef;
  readonly isDetailFresh: boolean;
}) {
  const command = usePullRequestSerializedCommand(props.environmentId, props.reference);
  const canReact = props.detail.capabilities.reactions === true && props.isDetailFresh;
  const canEdit = canEditPullRequestComment({
    canEditComments: props.detail.capabilities.edit?.comment === true && props.isDetailFresh,
    viewer: props.detail.viewer,
    author: props.comment.author?.login,
    kind: props.comment.kind,
  });
  const action = <PullRequestFixCommentAction comment={props.comment} />;

  return (
    <div className="rounded-lg border border-border/60 p-3 [contain-intrinsic-block-size:120px] [content-visibility:auto]">
      <PullRequestTimelineItem
        presentation="card"
        actor={props.comment.author?.login ?? "ghost"}
        at={props.comment.createdAt}
        title={summaryCommentTitle(props.comment)}
        body={props.comment.body}
        markdown
        reactions={props.comment.reactions}
        reactionsEnabled={canReact}
        reactionsPending={command.isPending}
        canEdit={canEdit}
        editPending={command.isPending}
        action={action}
        onSave={(body) =>
          command.execute(async () => {
            await cmmdApiClient.updatePullRequestComment({
              ...commandPullRequestRef(props.reference),
              idempotencyKey: crypto.randomUUID(),
              commentId: props.comment.id,
              kind: props.comment.kind as "issue-comment" | "review-comment",
              body,
            });
          })
        }
        onToggleReaction={(content, reacted) =>
          command.mutate(async () => {
            await cmmdApiClient.setPullRequestReaction({
              ...commandPullRequestRef(props.reference),
              idempotencyKey: crypto.randomUUID(),
              subjectId: props.comment.id,
              content,
              reacted,
            });
          })
        }
      />
    </div>
  );
}

function PullRequestFixCommentAction(props: { readonly comment: PullRequestComment }) {
  const handoff = usePullRequestHandoffController();
  const isFinding = props.comment.kind === "review" || props.comment.kind === "review-comment";
  if (handoff === null || !isFinding || props.comment.body.trim().length === 0) return null;
  return (
    <Button
      type="button"
      size="xs"
      variant="ghost"
      disabled={handoff.pendingAction !== null}
      onClick={() => void handoff.run("fix-finding", { kind: "remark", remark: props.comment })}
    >
      <HammerIcon aria-hidden className="size-3" />
      {handoff.currentThreadMatches ? "Fix in this thread" : "Fix in a new thread"}
    </Button>
  );
}

function summaryCommentTitle(comment: PullRequestComment): string {
  const state = comment.reviewState?.toLowerCase().replaceAll("_", " ");
  if (state !== undefined && state.length > 0) return state;
  if (comment.path !== null) return `commented on ${comment.path}`;
  return "commented";
}
